import { Popover, Transition } from "@headlessui/react";
import {
  ChevronRightIcon,
  GlobeAsiaAustraliaIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import {
  selectBookings,
  setBookings,
} from "application/features/bookings/bookingsSlice";
import moment from "moment";
import React, { Fragment, useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { stockTypeToSlug, stockDetailPath } from "data/stock_types";
import { StockNumericType } from "data/enums";
import { useWindowScroll } from "react-use";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import { useTranslation } from "react-i18next";
import MultiStockCard from "components/Quasi/MultiStockCard/MultiStockCard";
import type { BookingDataType } from "data/interfaces";
import { isStay, isExperience, isHolidayPackage } from "data/interfaces";

interface MyTripProps {
  // Define the props for your component here
}

const MyTrip: React.FC<MyTripProps> = () => {
  const { t, ready } = useTranslation("translation");
  const bookings = useSelector(selectBookings);
  const { y } = useWindowScroll();
  const popoverButton = useRef<HTMLButtonElement>(null);
  const [show, setShow] = React.useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const handleRemove = (booking_id: string | number) => {
    // remove from bookings list by booking id
    const newBookings = bookings.filter(
      (booking: BookingDataType) => booking?.id !== booking_id,
    );
    // update bookings list
    dispatch(setBookings(newBookings));
  };

  useEffect(() => {
    if (y > 0) {
      if (show) {
        popoverButton.current?.click();
      }
    }
  }, [y]);

  if (!ready) {
    return null; // or a loading spinner
  }

  return (
    // JSX code for your component's UI goes here
    <Popover>
      {({ open }) => (
        <>
          <Popover.Button
            ref={popoverButton}
            onClick={() => setShow(!open)}
            className="flex flex-row items-center gap-2 bg-neutral-100 dark:bg-neutral-800 dark:text-neutral-200 py-2 px-4 rounded-full cursor-pointer hover:opacity-80"
          >
            <GlobeAsiaAustraliaIcon className="h-6 w-6" />
            <span>{t("translation:navbar.myTripClientText")}</span>

            {bookings?.length > 0 && (
              <span className="text-white text-xs bg-red-400 rounded-full px-3 py-1">
                {bookings.length}
              </span>
            )}
          </Popover.Button>
          {}
          <Transition
            as={Fragment}
            show={open}
            enter="transition duration-100 ease-out"
            enterFrom="transform scale-95 opacity-0"
            enterTo="transform scale-100 opacity-100"
            leave="transition duration-75 ease-out"
            leaveFrom="transform scale-100 opacity-100"
            leaveTo="transform scale-95 opacity-0"
          >
            <Popover.Panel
              className={`fixed bg-white w-screen left-0 z-[99] mt-5 rounded-b-lg shadow-lg px-10 py-4`}
            >
              {bookings && bookings.length === 0 ? (
                <div className="flex flex-col items-center justify-center gap-4">
                  <span>{t("translation:navbar.myTripClientNoBookings")}</span>
                  <ButtonPrimary
                    onClick={() => (window.location.href = `/${stockTypeToSlug(StockNumericType.STAY)}`)}
                  >
                    {t("translation:navbar.myTripClientNoBookingsCallToAction")}
                  </ButtonPrimary>
                </div>
              ) : (
                <>
                  <h3 className="text-lg font-bold mb-4">
                    {t("translation:navbar.myTripClientBookings")}
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-4 max-h-[60vh] overflow-y-auto">
                    {bookings?.map((booking: BookingDataType) => {
                      // Extract data based on booking type
                      let cover: string = "";
                      let images: string[] = [];
                      let uuid: string | number = "";
                      let href: string = "";
                      let address: string = "";
                      let annotation: string = "";

                      if (isStay(booking.item)) {
                        uuid = booking.item.property_uuid;
                        cover =
                          booking.item.cover || booking.item.place_cover || "";
                        const galleryImages = booking.item.gallery_images || [];
                        images =
                          galleryImages.length > 0
                            ? galleryImages
                            : cover
                              ? [cover]
                              : [];
                        href = stockDetailPath(StockNumericType.STAY, uuid);
                        annotation = `${booking.item.property_type}`;
                        address = booking.item.address;
                      } else if (isExperience(booking.item)) {
                        uuid = booking.item.experience_uuid;
                        cover =
                          booking.item.cover ||
                          booking.item.featuredImage ||
                          "";
                        const galleryImages = booking.item.gallery_images || [];
                        images =
                          galleryImages.length > 0
                            ? galleryImages
                            : cover
                              ? [cover]
                              : [];
                        href = stockDetailPath(StockNumericType.EXPERIENCE, uuid);
                        annotation = `${booking.item.type}`;
                        address = booking.item.city;
                      } else if (isHolidayPackage(booking.item)) {
                        uuid = booking.item.package_uuid;
                        cover = booking.item.cover_image || "";
                        const galleryImages = booking.item.gallery_images || [];
                        images =
                          galleryImages.length > 0
                            ? galleryImages
                            : cover
                              ? [cover]
                              : [];
                        address = booking.item.location || "";
                        annotation = booking.item.category
                          ? `${booking.item.category}`
                          : `Pacchetto Vacanze`;
                        href = stockDetailPath(StockNumericType.HOLIDAY_PACKAGE, uuid);
                      } else {
                        return null;
                      }

                      return (
                        <MultiStockCard
                          key={booking.id}
                          id={uuid}
                          cover={cover}
                          annotation={annotation}
                          images={images}
                          title={booking.item.name}
                          address={address}
                          mood={booking.item.mood}
                          price={undefined}
                          href={href}
                          orientation="vertical"
                          loading={false}
                        >
                          <div className="flex flex-col gap-2 w-full">
                            <div className="flex items-center justify-between text-sm">
                              <span className="text-neutral-600 dark:text-neutral-400">
                                {moment(booking.startDate).format("DD-MM-YYYY")}
                              </span>
                              <span className="text-neutral-600 dark:text-neutral-400">
                                →
                              </span>
                              <span className="text-neutral-600 dark:text-neutral-400">
                                {moment(booking.endDate).format("DD-MM-YYYY")}
                              </span>
                            </div>
                            <button
                              className="flex flex-row items-center justify-center text-red-500 gap-2 hover:opacity-80 px-3 py-2 bg-neutral-100 dark:bg-neutral-800 dark:text-neutral-200 rounded-full w-full"
                              onClick={() => handleRemove(booking.id)}
                            >
                              <TrashIcon className="h-4 w-4" />
                              <span className="text-sm">
                                {t("translation:navbar.myTripRemoveText")}
                              </span>
                            </button>
                          </div>
                        </MultiStockCard>
                      );
                    })}
                  </div>
                  <div className="flex justify-end mt-4 pt-4 border-t border-neutral-200 dark:border-neutral-700">
                    {bookings.length ? (
                      <ButtonPrimary
                        onClick={() => navigate("/client/booking/calendar")}
                      >
                        {t(
                          "translation:navbar.myTripClientBookingsCallToAction",
                        )}
                        <ChevronRightIcon className="h-5 w-5" />
                      </ButtonPrimary>
                    ) : null}
                  </div>
                </>
              )}
            </Popover.Panel>
          </Transition>
        </>
      )}
    </Popover>
  );
};

export default MyTrip;
